import { DATE_RANGE_TYPE } from '@hotjar/webapp/constants/DateFilterRanges';

import type { User as Author } from '../AuthorFilter';

function isObject(value: unknown): value is object {
  return !!value && typeof value === 'object' && value !== null;
}

export function parseWithCustomParserAndDefaultValue<TValue>(
  jsonValue: string | null,
  defaultValue: TValue,
  parser: (jsonValue: string) => TValue,
): TValue {
  if (!jsonValue) {
    return defaultValue;
  }

  try {
    return parser(JSON.parse(jsonValue));
  } catch {
    return defaultValue;
  }
}

export function hasAuthorProps(
  value: unknown,
): value is { avatar: unknown; email: unknown; id: unknown; name: unknown } {
  return (
    !!value &&
    typeof value === 'object' &&
    value !== null &&
    'avatar' in value &&
    'email' in value &&
    'id' in value &&
    'name' in value
  );
}

export function isValidAuthor(value: unknown): boolean {
  return (
    hasAuthorProps(value) &&
    (value.avatar === null || typeof value.avatar === 'string') &&
    (value.email === null || typeof value.email === 'string') &&
    typeof value.id === 'number' &&
    typeof value.name === 'string'
  );
}

export function isValidAuthors(value: unknown): value is Author[] {
  return value instanceof Array && value.every(isValidAuthor);
}

export function isValidDate(value: unknown): boolean {
  return (
    typeof value === 'string' &&
    !Number.isNaN(Date.parse(value)) &&
    value === new Date(value).toISOString()
  );
}

export function hasType(value: unknown): value is { type: unknown } {
  return isObject(value) && 'type' in value;
}

export function hasFromAndTo(value: unknown): value is { from: unknown; to: unknown } {
  return isObject(value) && 'from' in value && 'to' in value;
}

export function hasAmountAndUnit(value: unknown): value is { amount: unknown; unit: unknown } {
  return isObject(value) && 'amount' in value && 'unit' in value;
}

export function isAllDateType(value: unknown): boolean {
  return hasType(value) && typeof value.type === 'string' && value.type === DATE_RANGE_TYPE.ALL;
}

export function isAbsoluteDateType(
  value: unknown,
): value is { type: DATE_RANGE_TYPE.ABSOLUTE; from: string; to: string } {
  return (
    hasType(value) &&
    typeof value.type === 'string' &&
    value.type === DATE_RANGE_TYPE.ABSOLUTE &&
    hasFromAndTo(value) &&
    isValidDate(value.from) &&
    isValidDate(value.to)
  );
}

export function isRelativeDateType(value: unknown): boolean {
  return (
    hasType(value) &&
    typeof value.type === 'string' &&
    value.type === DATE_RANGE_TYPE.RELATIVE &&
    hasAmountAndUnit(value) &&
    typeof value.amount === 'number' &&
    typeof value.unit === 'string' &&
    ['hours', 'days', 'months'].includes(value.unit)
  );
}

export function isValidDateRange(value: unknown): boolean {
  return isAllDateType(value) || isAbsoluteDateType(value) || isRelativeDateType(value);
}

export function isValidLabels(value: unknown): value is string[] {
  return value instanceof Array && value.every((item: unknown) => typeof item === 'string');
}

export function isValidSources(value: unknown): value is ('heatmap' | 'recording')[] {
  return (
    value instanceof Array &&
    value.every(
      (item: unknown) => typeof item === 'string' && ['heatmap', 'recording'].includes(item),
    )
  );
}

export function isValidStatus(value: string | null): boolean {
  if (value === null) return true;
  return ['both', 'solved', 'unsolved'].includes(value);
}
